// Per-scene choreography. Layer names are the Figma layer names baked into the SVG ids
// (`<prefix>__<layer name>`), so a scene keeps working as long as its export keeps those layers.

import { CHANGELOG_SCENES, ChangelogSceneKey } from "./sceneConfig";
import {
  EASE_OUT,
  NUM,
  bars,
  byId,
  byIds,
  children,
  countUp,
  draw,
  fade,
  grow,
  pop,
  rise,
  rows,
  slideX,
  stagger,
  strokes,
  textsMatching,
  typewriter,
  wipe,
} from "./sceneMotion";

type SceneRun = (svg: SVGSVGElement, prefix: string) => void;

const findText = (svg: SVGSVGElement, needle: string) =>
  Array.from(svg.querySelectorAll<SVGTextElement>("text")).find((text) => text.textContent?.includes(needle));

const SCENE_RUNS: Record<ChangelogSceneKey, SceneRun> = {
  accounts(svg, p) {
    byIds(svg, p, ["Username Header_4", "Group 1000003066", "Group 1000003067", "Group 1000003068"]).forEach((el, i) =>
      fade(el, { delay: stagger(i, 60), duration: 500 }),
    );
    const list = Array.from(svg.querySelectorAll<SVGElement>(`[id^="${CSS.escape(`${p}__List / accounts`)}"]`)).filter((el) =>
      /accounts(_\d+)?$/.test(el.id),
    );
    const tag = byId(svg, p, "Tag / emotion");
    rows(tag ? [tag, ...list] : list, 220, 70);
    textsMatching(byId(svg, p, "Frame 2147239803") ?? svg, /\d[\d.,]+ USD/).forEach((el, i) =>
      countUp(el, { delay: stagger(i, 70, 300), duration: 1200 }),
    );
  },

  "trading-journal"(svg, p) {
    rise(byId(svg, p, "Group 2085665225"), { duration: 700, distance: 10 });
    rows(children(byId(svg, p, "Frame 2147239299")), 300, 45);
    pop(byId(svg, p, "Frame 36"), { delay: 150 });
  },

  "daily-diary"(svg, p) {
    const top = byId(svg, p, "Frame 2147238245");
    const bottom = byId(svg, p, "Frame 2147238246");
    rise(top, { duration: 700 });
    // Heatmap dots pop in a scattered (not row-by-row) order.
    const dots = Array.from(byId(svg, p, "Group 43")?.querySelectorAll<SVGCircleElement>("circle") ?? []);
    dots
      .map((dot, i) => ({ dot, order: (37 * i) % dots.length }))
      .forEach(({ dot, order }) => pop(dot, { delay: 350 + 22 * order, duration: 420 }));
    pop(byId(svg, p, "Group 42"), { delay: 1500, origin: "50% 100%" });
    rise(bottom, { delay: 600, duration: 800, distance: 24 });
    bars(bottom, 60).forEach((el, i) => grow(el, { delay: stagger(i, 90, 1000), duration: 900 }));
    fade(byId(svg, p, "Line 19"), { delay: 1700 });
    pop(byId(svg, p, "Frame 35_2"), { delay: 1900, origin: "50% 100%" });
  },

  strategy(svg, p) {
    textsMatching(svg, NUM, 7).forEach((el, i) => countUp(el, { delay: stagger(i, 40, 200), duration: 1400 }));
    draw(byId(svg, p, "Shape_4"), { delay: 400, duration: 2000 });
    wipe(byId(svg, p, "Group 1000002996"), { delay: 400, duration: 2000 });
    children(byId(svg, p, "Frame 32")).forEach((el, i) => fade(el, { delay: stagger(i, 60, 300), duration: 600 }));
    pop(byId(svg, p, "Group 23"), { delay: 2300, origin: "50% 100%" });
  },

  dashboard(svg, p) {
    const cards = byIds(svg, p, [
      "Frame 57",
      "Frame 2147238232",
      "Frame 2147238234",
      "Frame 2147238236",
      "Frame 63",
      "Frame 2147238254",
      "Frame 64",
      "Frame 66",
      "Frame 2147238241",
    ]);
    cards.forEach((el, i) => rise(el, { delay: stagger(i, 90, 150), duration: 650, distance: 16 }));
    byIds(svg, p, ["Frame 73", "Frame 2147239931", "Frame 2147239932", "Frame 2147239933"]).forEach((el, i) =>
      slideX(el, { delay: stagger(i, 80, 100), dx: -14 }),
    );
    byIds(svg, p, ["Frame 2147239292", "Swatches", "Frame 101", "Frame 2147239936", "Frame 2147238253_2"]).forEach((el, i) =>
      slideX(el, { delay: stagger(i, 70, 300), dx: 14 }),
    );
    strokes(byId(svg, p, "Frame 63") ?? svg, 150).forEach((el) => draw(el, { delay: 900, duration: 1800 }));
    bars(byId(svg, p, "Frame 66")).forEach((el, i) => grow(el, { delay: stagger(i, 70, 1100), duration: 800 }));
    Array.from(byId(svg, p, "Frame 2147238241")?.querySelectorAll<SVGElement>("circle, ellipse") ?? []).forEach((el, i) =>
      pop(el, { delay: 1200 + ((29 * i) % 47) * 18, duration: 380 }),
    );
    cards.forEach((card) => textsMatching(card, NUM, 8).forEach((el) => countUp(el, { delay: 700, duration: 1300 })));
  },

  "morning-brief"(svg, p) {
    rise(byId(svg, p, "quiz_score_4"), { duration: 700, distance: 18 });
    grow(byId(svg, p, "Rectangle 1430107536"), { axis: "x", delay: 400, duration: 1200 });
    byIds(svg, p, ["Group 2085665408", "Group 2085665409", "Group 2085665405", "Group 2085665406", "Group 2085665407"]).forEach(
      (el, i) => pop(el, { delay: stagger(i, 90, 600) }),
    );
    pop(byId(svg, p, "Ellipse 86"), { delay: 1500, duration: 600 });
    draw(byId(svg, p, "Vector 151"), { delay: 1800, duration: 450, easing: EASE_OUT });
    rise(byId(svg, p, "button"), { delay: 1900, duration: 500, distance: 6 });
  },

  "session-wrapped"(svg, p) {
    byIds(svg, p, ["Quadro 1", "Quadro 2", "Quadro 3"]).forEach((el, i) =>
      rise(el, { delay: stagger(i, 160), duration: 800, distance: 28 }),
    );
    ["", "_2", "_3"].forEach((suffix, i) => {
      const bar = byId(svg, p, `Rectangle 1430107536${suffix}`) ?? byId(svg, p, `Rectangle 1430107535${suffix}`);
      grow(bar, { axis: "x", delay: stagger(i, 160, 500), duration: 1600 });
    });
    textsMatching(svg, NUM).forEach((el, i) => countUp(el, { delay: stagger(i, 60, 600), duration: 1500 }));
    draw(byId(svg, p, "Vector 144"), { delay: 1000, duration: 1400 });
    byIds(svg, p, ["Frame 2147240230", "Frame 2147240229", "Frame 2147240228"]).forEach((el, i) =>
      pop(el, { delay: stagger(i, 450, 1000) }),
    );
    byIds(svg, p, ["Frame 2147240231", "Frame 2147240232", "Frame 2147240233"]).forEach((el, i) =>
      pop(el, { delay: stagger(i, 450, 1150), origin: "50% 100%" }),
    );
  },

  "ts-coach"(svg, p) {
    fade(svg, { duration: 500 });
    const greeting = findText(svg, "Hey Felipe");
    if (greeting) typewriter(greeting, { delay: 400, speed: 40 });
    const prompt = findText(svg, "Ask ts coach about trading");
    if (prompt) typewriter(prompt, { delay: 1500, speed: 30 });
    rise(byId(svg, p, "Group 2085665436"), { delay: 1200, duration: 600, distance: 10 });
    byIds(svg, p, ["How was my performance ...", "When do I trade my best?", "Which strategy makes me the ..."]).forEach((el, i) =>
      slideX(el, { delay: stagger(i, 120, 700), dx: -10 }),
    );
    children(byId(svg, p, "Frame 2147240272")).forEach((el, i) => rise(el, { delay: stagger(i, 110, 2100) }));
  },
};

/**
 * (Re)plays a scene from its first frame: invalidates running text loops, cancels running
 * animations, restores any text a previous run rewrote, then runs the choreography.
 * Resolves when every animation of this run has finished or been cancelled.
 */
export function playChangelogScene(svg: SVGSVGElement, key: ChangelogSceneKey) {
  svg.dataset.run = String(Number(svg.dataset.run ?? 0) + 1);
  svg.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  svg.querySelectorAll<SVGElement>("[data-final]").forEach((el) => (el.textContent = el.dataset.final ?? ""));
  SCENE_RUNS[key](svg, CHANGELOG_SCENES[key].idPrefix);
  return Promise.allSettled(svg.getAnimations({ subtree: true }).map((animation) => animation.finished)).then(() => undefined);
}

/** Stops a scene's text loops (used on unmount). */
export function stopChangelogScene(svg: SVGSVGElement) {
  svg.dataset.run = String(Number(svg.dataset.run ?? 0) + 1);
}
